import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import { usePinGate, PIN_SESSION_STORAGE_KEY } from './usePinGate';

// Types
interface User {
  id: number;
  username: string;
  displayName: string;
  createdAt: string;
  lastLogin?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tokenReady: boolean; // Indicates if JWT token is stored and ready for API calls
}

interface AuthContextType extends AuthState {
  register: (username: string) => Promise<boolean>;
  login: (username?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuthStatus: () => Promise<void>;
}

// API is served same-origin via Pages Functions (/api/*).
// VITE_API_BASE_URL may override for local dev against `wrangler dev`.
const API_BASE: string = import.meta.env.VITE_API_BASE_URL || '';


// Auth context
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook for using auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    tokenReady: false,
  });
  const { clearPinSession, refreshStatus, pinUrl, sessionToken } = usePinGate();

  /**
   * Read the PIN session token AT CALL TIME.
   *
   * Do NOT prefer the context `sessionToken` here: this function is called
   * from useCallback closures (login/register) whose deps don't include the
   * token, so the captured value can be a token that was already expired and
   * replaced when the user re-entered the PIN. Observed bug: "first passkey
   * click after PIN entry → PIN session expired; F5 fixes it" — F5 remounts
   * and re-reads localStorage, which is always current.
   */
  const readCurrentPinSession = useCallback((): string | null => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.sessionStorage.getItem(PIN_SESSION_STORAGE_KEY);
        if (stored) return stored;
      } catch { /* private mode */ }
    }
    return sessionToken ?? null;
  }, [sessionToken]);

  // Helper function to update state
  const updateState = useCallback((updates: Partial<AuthState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Backend error responses use { success: false, error: string } (see
   * index.ts onError). Older code read `message` and always fell back to a
   * generic string, hiding real causes like "PIN session expired".
   */
  const readApiError = async (res: Response, fallback: string): Promise<string> => {
    try {
      const data = await res.json();
      return data.error || data.message || fallback;
    } catch {
      return fallback;
    }
  };

  /**
   * POST helper for auth begin/finish steps with retry on transient auth
   * failures. Cloudflare KV is eventually consistent: a PIN session minted
   * on one edge colo can briefly read as missing from another (mobile
   * networks switch colos between requests), producing a one-off 401.
   * Retrying 401/423 a couple of times absorbs that lag.
   */
  const authPostWithRetry = async (
    url: string,
    headers: Record<string, string>,
    body: unknown,
    { attempts = 3, retryDelayMs = 800 }: { attempts?: number; retryDelayMs?: number }
  ): Promise<Response> => {
    let lastRes: Response | null = null;
    for (let attempt = 0; attempt < attempts; attempt++) {
      const res = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });
      // Retry only transient auth failures (KV replication lag), not
      // semantic rejections like 403 "Registration is disabled" or 400s
      if (res.status !== 401 && res.status !== 423) return res;
      lastRes = res;
      if (attempt < attempts - 1) {
        await new Promise((r) => setTimeout(r, retryDelayMs));
        // Re-read the PIN session at retry time (localStorage first — the
        // context token may be a stale closure capture)
        const pinSession = readCurrentPinSession();
        if (pinSession) headers = { ...headers, 'X-Pin-Session': pinSession };
      }
    }
    return lastRes!;
  };

  // Clear error
  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  // Set error
  const setError = useCallback((error: string) => {
    updateState({ error, isLoading: false });
  }, [updateState]);

  // Check if user is authenticated
  const checkAuthStatus = useCallback(async () => {
    try {
      updateState({ isLoading: true, error: null });

      const token = localStorage.getItem('jwt_token'); // legacy cleanup only
      const pinSession = readCurrentPinSession();
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(pinSession ? { 'X-Pin-Session': pinSession } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Authenticated via HttpOnly cookie — tokenReady immediately
        updateState({
          user: data.user,
          isAuthenticated: true,
          isLoading: false,
          tokenReady: true,
        });
      } else {
        updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          tokenReady: false,
        });
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        tokenReady: false,
        error: 'Failed to check authentication status',
      });
    }
  }, [updateState]);

  // Register new user
  const register = useCallback(async (username: string): Promise<boolean> => {
    try {
      updateState({ isLoading: true, error: null });

      // Check WebAuthn support
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      const pinSession = readCurrentPinSession();
      const pinHeaders: Record<string, string> = pinSession
        ? { 'X-Pin-Session': pinSession }
        : {};

      // Step 1: Begin registration
      const beginResponse = await authPostWithRetry(
        `${API_BASE}/api/auth/register/begin`,
        { 'Content-Type': 'application/json', ...pinHeaders },
        { username },
        {}
      );

      if (!beginResponse.ok) {
        throw new Error(await readApiError(beginResponse, 'Registration failed'));
      }

      const { options, challengeId } = await beginResponse.json();

      // Step 2: Start WebAuthn registration
      const credential = await startRegistration({ optionsJSON: options });

      // Step 3: Finish registration
      const finishResponse = await fetch(`${API_BASE}/api/auth/register/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...pinHeaders },
        credentials: 'include',
        body: JSON.stringify({
          challengeId,
          username,
          response: credential,
        }),
      });

      if (!finishResponse.ok) {
        throw new Error(await readApiError(finishResponse, 'Registration verification failed'));
      }

      const result = await finishResponse.json();

      // JWT lives in the HttpOnly cookie set by login/finish — deliberately
      // NOT stored in JS-readable storage (XSS cannot exfiltrate it).
      updateState({
        user: result.user,
        isAuthenticated: true,
        isLoading: false,
        tokenReady: true,
      });

      return true;
    } catch (error) {
      console.error('Registration error:', error);
      setError(error instanceof Error ? error.message : 'Registration failed');
      return false;
    }
  }, [updateState, setError]);

  // Login user
  const login = useCallback(async (username?: string): Promise<boolean> => {
    try {
      updateState({ isLoading: true, error: null });

      // Check WebAuthn support
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      // Step 1: Begin authentication
      const pinSession = readCurrentPinSession();

      const beginHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (pinSession) {
        beginHeaders['X-Pin-Session'] = pinSession;
      }

      const beginQuery = pinSession ? `?pin_session_token=${encodeURIComponent(pinSession)}` : '';
      const beginResponse = await authPostWithRetry(
        `${API_BASE}/api/auth/login/begin${beginQuery}`,
        beginHeaders,
        { username },
        {}
      );

      if (!beginResponse.ok) {
        throw new Error(await readApiError(beginResponse, 'Login failed'));
      }

      const { options, challengeId } = await beginResponse.json();

      // Step 2: Start WebAuthn authentication
      const credential = await startAuthentication({ optionsJSON: options });

      // Step 3: Finish authentication
      const finishHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (pinSession) {
        finishHeaders['X-Pin-Session'] = pinSession;
      }

      const finishQuery = pinSession ? `?pin_session_token=${encodeURIComponent(pinSession)}` : '';
      const finishResponse = await fetch(`${API_BASE}/api/auth/login/finish${finishQuery}`, {
        method: 'POST',
        headers: finishHeaders,
        credentials: 'include',
        body: JSON.stringify({
          challengeId,
          response: credential,
        }),
      });

      if (!finishResponse.ok) {
        throw new Error(await readApiError(finishResponse, 'Authentication failed'));
      }

      const result = await finishResponse.json();

      // JWT lives in the HttpOnly cookie set by login/finish — deliberately
      // NOT stored in JS-readable storage (XSS cannot exfiltrate it).
      updateState({
        user: result.user,
        isAuthenticated: true,
        isLoading: false,
        tokenReady: true,
      });
      

      return true;
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific WebAuthn errors
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          setError('Authentication was cancelled or not allowed');
        } else if (error.name === 'InvalidStateError') {
          setError('Authenticator is already registered');
        } else if (error.name === 'NotSupportedError') {
          setError('WebAuthn is not supported');
        } else {
          setError(error.message || 'Login failed');
        }
      } else {
        setError('Login failed');
      }
      
      return false;
    }
  }, [updateState, setError]);

  // Logout user
  const logout = useCallback(async () => {
    updateState({ isLoading: true, error: null });
    let logoutError: string | null = null;

    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
      logoutError = 'Logout may have failed';
    } finally {
      localStorage.removeItem('jwt_token');
      try {
        await clearPinSession();
      } catch (err) {
        console.warn('Failed to clear PIN session during logout', err);
      }
      try {
        await refreshStatus();
      } catch (err) {
        console.warn('Failed to refresh PIN status during logout', err);
      }

      updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        tokenReady: false,
        error: logoutError,
      });

      if (typeof window !== 'undefined') {
        window.location.replace(pinUrl || '/pin');
      }
    }
  }, [clearPinSession, pinUrl, refreshStatus, updateState]);

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const contextValue: AuthContextType = {
    ...state,
    register,
    login,
    logout,
    clearError,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Hook to check WebAuthn support
export const useWebAuthnSupport = () => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkSupport = async () => {
      try {
        // Check for basic WebAuthn support
        if (!window.PublicKeyCredential) {
          setIsSupported(false);
          return;
        }

        // Check for conditional UI support (optional)
        const conditionalUISupported = await PublicKeyCredential.isConditionalMediationAvailable?.();
        
        setIsSupported(true);
        console.log('WebAuthn support:', {
          basicSupport: true,
          conditionalUI: conditionalUISupported,
        });
      } catch (error) {
        console.error('WebAuthn support check failed:', error);
        setIsSupported(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSupport();
  }, []);

  return { isSupported, isLoading };
};
